import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect, FormEvent, useRef } from 'react'
import {
  LayoutDashboard,
  BookOpen,
  Award,
  User,
  Menu,
  X,
  LogOut,
  Video,
  CreditCard,
  ShoppingCart,
  Heart,
  ChevronLeft,
  ChevronRight,
  Settings,
  Bell,
  Search,
  Sparkles,
  MoreVertical
} from 'lucide-react'
import logo from '../../assets/logo.jpeg'

const navigation = [
  { name: 'Dashboard', href: '/user/dashboard', icon: LayoutDashboard },
  { name: 'My Courses', href: '/user/courses', icon: BookOpen },
  { name: 'Live Classes', href: '/user/live-sessions', icon: Video },
  { name: 'Certificates', href: '/user/certificates', icon: Award },
  { name: 'Wishlist', href: '/user/wishlist', icon: Heart },
  { name: 'Cart', href: '/user/cart', icon: ShoppingCart },
  { name: 'Payments', href: '/user/payments', icon: CreditCard },
]

const profileNavigation = [
  { name: 'Profile', href: '/user/dashboard/profile', icon: User },
]

export default function UserLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [user, setUser] = useState<any>(null)
  const [cartCount, setCartCount] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [suggestions, setSuggestions] = useState<any[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const searchRef = useRef<HTMLFormElement>(null)

  const handleSearch = (e: FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/user/courses?search=${encodeURIComponent(searchQuery)}`)
      setShowSuggestions(false)
    }
  }

  // Debounce search suggestions
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchQuery.trim().length > 1) {
        try {
          const response = await fetch(`/api/courses?search=${encodeURIComponent(searchQuery)}&limit=5`)
          if (response.ok) {
            const data = await response.json()
            setSuggestions(data.courses)
            setShowSuggestions(true)
          }
        } catch (error) {
          console.error('Error fetching suggestions:', error)
        }
      } else {
        setSuggestions([])
        setShowSuggestions(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [searchQuery])

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    const token = localStorage.getItem('token')
    const userString = localStorage.getItem('user')

    if (!token || !userString) {
      navigate('/user/login')
      return
    }

    const userData = JSON.parse(userString)
    setUser(userData)

    if (userData.role === 'ADMIN' || userData.role === 'admin') {
      navigate('/admin')
      return
    }
  }, [navigate])

  // Load and track cart count
  useEffect(() => {
    const updateCartCount = () => {
      try {
        const cart = JSON.parse(localStorage.getItem('userCart') || '[]')
        setCartCount(cart.length)
      } catch (e) {
        setCartCount(0)
      }
    }

    // Initial load
    updateCartCount()

    // Listen for cart updates
    const handleCartUpdate = (e: CustomEvent) => {
      if (e.detail?.count !== undefined) {
        setCartCount(e.detail.count)
      } else {
        updateCartCount()
      }
    }

    window.addEventListener('cart-updated', handleCartUpdate as EventListener)
    window.addEventListener('storage', updateCartCount)

    return () => {
      window.removeEventListener('cart-updated', handleCartUpdate as EventListener)
      window.removeEventListener('storage', updateCartCount)
    }
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    navigate('/user/login')
  }

  const isActive = (path: string) => location.pathname === path

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0F19] flex font-sans text-slate-900 dark:text-slate-100 selection:bg-violet-500/30 overflow-x-hidden">

      {/* MOBILE HEADER */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <img src={logo} alt="Logo" className="w-8 h-8 rounded-lg object-cover shadow-lg shadow-violet-500/20" />
            <span className="font-bold text-lg text-slate-900 dark:text-white">Sudha's Academy</span>
          </div>
        </div>
        <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-700 dark:text-slate-300 font-bold text-sm">
          {(user?.name || 'S')[0].toUpperCase()}
        </div>
      </div>

      {/* MOBILE MENU OVERLAY */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
          <div className="absolute top-0 left-0 bottom-0 w-72 bg-white dark:bg-[#111625] border-r border-slate-200 dark:border-slate-800 p-6 shadow-2xl flex flex-col">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <img src={logo} alt="Logo" className="w-10 h-10 rounded-xl object-cover shadow-lg shadow-violet-500/20" />
                <div>
                  <h3 className="font-bold text-slate-900 dark:text-white">Sudha's Academy</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Learning Platform</p>
                </div>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <nav className="space-y-1 flex-1 overflow-y-auto custom-scrollbar">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4 px-2">Menu</div>
              {navigation.map((item) => {
                const Icon = item.icon
                return (
                  <button
                    key={item.name}
                    onClick={() => {
                      navigate(item.href)
                      setMobileMenuOpen(false)
                    }}
                    className={`w-full flex items-center px-4 py-3.5 text-sm font-medium rounded-xl transition-all ${isActive(item.href)
                      ? 'bg-violet-600 text-white shadow-lg shadow-violet-500/30'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                  >
                    <Icon className="h-5 w-5 mr-3" />
                    {item.name}
                  </button>
                )
              })}

              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4 mt-8 px-2">Account</div>
              {profileNavigation.map((item) => {
                const Icon = item.icon
                return (
                  <button
                    key={item.name}
                    onClick={() => {
                      navigate(item.href)
                      setMobileMenuOpen(false)
                    }}
                    className={`w-full flex items-center px-4 py-3.5 text-sm font-medium rounded-xl transition-all ${isActive(item.href)
                      ? 'bg-violet-600 text-white shadow-lg shadow-violet-500/30'
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                  >
                    <Icon className="h-5 w-5 mr-3" />
                    {item.name}
                  </button>
                )
              })}
            </nav>

            <div className="pt-6 border-t border-slate-100 dark:border-slate-800 mt-4">
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-4 py-3.5 text-sm font-medium rounded-xl text-red-600 bg-red-50 dark:bg-red-900/10 hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors"
              >
                <LogOut className="h-5 w-5 mr-3" />
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DESKTOP SIDEBAR - Modern Floating Glassmorphism Style */}
      <aside
        className={`hidden lg:flex flex-col fixed top-4 bottom-4 left-4 z-40 bg-white/90 dark:bg-[#1A1F2E]/95 backdrop-blur-xl border border-slate-200/60 dark:border-slate-700/50 shadow-2xl shadow-slate-200/50 dark:shadow-black/50 rounded-[2.5rem] transition-all duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)] ${sidebarOpen ? 'w-72' : 'w-[96px]'
          }`}
      >
        {/* Decorative Background Elements */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-violet-50/50 to-transparent dark:from-violet-900/10 rounded-t-[2.5rem] pointer-events-none"></div>

        {/* Logo Section */}
        <div className={`h-28 flex items-center relative z-10 transition-all duration-300 ${sidebarOpen ? 'px-8' : 'justify-center'}`}>
          <div className="flex items-center gap-4">
            <div className="relative group">
              <div className="absolute inset-0 bg-violet-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition-opacity duration-300"></div>
              <img src={logo} alt="Logo" className="w-12 h-12 rounded-2xl object-cover shadow-lg shadow-violet-500/20 relative z-10" />
            </div>

            <div className={`transition-all duration-300 overflow-hidden whitespace-nowrap ${sidebarOpen ? 'opacity-100 w-auto' : 'opacity-0 w-0'}`}>
              <h1 className="font-bold text-xl text-slate-900 dark:text-white tracking-tight">Sudha's</h1>
              <p className="text-xs font-medium text-violet-600 dark:text-violet-400 tracking-wide uppercase">Academy</p>
            </div>
          </div>
        </div>

        {/* Navigation Items */}
        <div className="flex-1 overflow-y-auto py-2 px-5 space-y-8 custom-scrollbar z-10">
          {/* Main Menu */}
          <div className="space-y-2">
            <div className={`text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4 px-4 transition-opacity duration-300 ${!sidebarOpen && 'opacity-0 hidden'}`}>
              Main Menu
            </div>
            {navigation.map((item) => {
              const Icon = item.icon
              const active = isActive(item.href)
              return (
                <button
                  key={item.name}
                  onClick={() => navigate(item.href)}
                  className={`w-full flex items-center h-12 rounded-2xl transition-all duration-300 group relative ${active
                    ? 'bg-violet-600 text-white shadow-lg shadow-violet-500/30'
                    : 'text-slate-500 dark:text-slate-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 hover:text-violet-700 dark:hover:text-violet-300'
                    } ${sidebarOpen ? 'px-4' : 'justify-center px-0'}`}
                  title={!sidebarOpen ? item.name : ''}
                >
                  <Icon className={`h-[22px] w-[22px] flex-shrink-0 transition-transform duration-300 ${active ? 'scale-100' : 'group-hover:scale-110'} ${sidebarOpen ? 'mr-3.5' : ''}`} strokeWidth={active ? 2.5 : 2} />

                  {sidebarOpen && (
                    <span className={`font-medium truncate ${active ? 'font-semibold' : ''}`}>{item.name}</span>
                  )}

                  {!sidebarOpen && active && (
                    <div className="absolute right-2 w-1.5 h-1.5 bg-white rounded-full" />
                  )}
                </button>
              )
            })}
          </div>

          {/* Account Section */}
          <div className="space-y-2">
            <div className={`text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4 px-4 transition-opacity duration-300 ${!sidebarOpen && 'opacity-0 hidden'}`}>
              Account
            </div>
            {profileNavigation.map((item) => {
              const Icon = item.icon
              const active = isActive(item.href)
              return (
                <button
                  key={item.name}
                  onClick={() => navigate(item.href)}
                  className={`w-full flex items-center h-12 rounded-2xl transition-all duration-300 group relative ${active
                    ? 'bg-violet-600 text-white shadow-lg shadow-violet-500/30'
                    : 'text-slate-500 dark:text-slate-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 hover:text-violet-700 dark:hover:text-violet-300'
                    } ${sidebarOpen ? 'px-4' : 'justify-center px-0'}`}
                  title={!sidebarOpen ? item.name : ''}
                >
                  <Icon className={`h-[22px] w-[22px] flex-shrink-0 transition-transform duration-300 ${active ? 'scale-100' : 'group-hover:scale-110'} ${sidebarOpen ? 'mr-3.5' : ''}`} strokeWidth={active ? 2.5 : 2} />
                  {sidebarOpen && <span className={`font-medium truncate ${active ? 'font-semibold' : ''}`}>{item.name}</span>}
                </button>
              )
            })}
          </div>
        </div>

        {/* User Profile Card */}
        <div className="p-5 z-10 mt-auto">
          <div className={`relative overflow-hidden rounded-2xl transition-all duration-300 ${sidebarOpen
            ? 'bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700 p-3'
            : 'bg-transparent p-0 flex justify-center'
            }`}>
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-white font-bold shadow-md cursor-pointer hover:scale-105 transition-transform flex-shrink-0">
                  {(user?.name || 'S')[0].toUpperCase()}
                </div>
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-slate-800 rounded-full"></div>
              </div>

              {sidebarOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{user?.name || 'User'}</p>
                  <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400 truncate uppercase tracking-wide">Student</p>
                </div>
              )}

              {sidebarOpen && (
                <button
                  onClick={handleLogout}
                  className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                  title="Logout"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Toggle Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute -right-4 top-1/2 -translate-y-1/2 w-8 h-16 bg-white dark:bg-[#1A1F2E] border border-slate-200 dark:border-slate-700 rounded-full flex items-center justify-center shadow-lg hover:scale-110 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-all z-50 text-slate-400 hover:text-violet-600 group"
        >
          {sidebarOpen ? (
            <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          ) : (
            <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          )}
        </button>
      </aside>

      {/* MAIN CONTENT WRAPPER */}
      <main
        className={`flex-1 transition-all duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)] flex flex-col min-h-screen ${sidebarOpen ? 'lg:ml-80' : 'lg:ml-32'
          } mt-16 lg:mt-0 w-full max-w-full overflow-x-hidden`}
      >
        {/* Desktop Header */}
        <header className="hidden lg:flex h-24 items-center justify-between px-8 sticky top-0 z-30 bg-[#F8FAFC]/80 dark:bg-[#0B0F19]/80 backdrop-blur-sm">
          <div className="flex items-center gap-4 flex-1">
            <form onSubmit={handleSearch} className="relative w-full max-w-md hidden xl:block" ref={searchRef}>
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search for courses, skills, or mentors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery.length > 1 && setShowSuggestions(true)}
                className="w-full pl-12 pr-4 py-3 bg-white dark:bg-[#1A1F2E] border-none rounded-2xl shadow-sm text-sm focus:ring-2 focus:ring-violet-500/20 text-slate-900 dark:text-white placeholder:text-slate-400 transition-all"
              />

              {/* Suggestions Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-[#1A1F2E] rounded-2xl shadow-xl border border-slate-100 dark:border-slate-800 overflow-hidden z-50">
                  {suggestions.map((course) => (
                    <button
                      key={course._id}
                      onClick={() => {
                        navigate(`/user/courses/${course._id}`)
                        setShowSuggestions(false)
                        setSearchQuery('')
                      }}
                      className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left"
                    >
                      <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 overflow-hidden flex-shrink-0 flex items-center justify-center">
                        {course.thumbnail ? (
                          <img src={course.thumbnail} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <BookOpen className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-slate-900 dark:text-white line-clamp-1">{course.title}</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{course.category?.name || 'Course'}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </form>
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/user/cart')}
              className="p-3 bg-white dark:bg-[#1A1F2E] text-slate-500 hover:text-violet-600 dark:hover:text-violet-400 rounded-xl shadow-sm hover:shadow-md transition-all relative group"
            >
              <ShoppingCart className="w-5 h-5 group-hover:scale-110 transition-transform" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-violet-600 text-[10px] font-bold text-white flex items-center justify-center rounded-full border-2 border-[#F8FAFC] dark:border-[#0B0F19] shadow-sm">
                  {cartCount}
                </span>
              )}
            </button>

            <button
              onClick={() => navigate('/user/dashboard/profile')}
              className="p-3 bg-white dark:bg-[#1A1F2E] text-slate-500 hover:text-violet-600 dark:hover:text-violet-400 rounded-xl shadow-sm hover:shadow-md transition-all relative group"
            >
              <Settings className="w-5 h-5 group-hover:scale-110 transition-transform" />
            </button>
          </div>
        </header>

        <div className="flex-1 px-4 md:px-8 pb-8 w-full max-w-full">
          <Outlet />
        </div>
      </main>
    </div>
  )
}